import { useClasses } from "../../hooks/useClasses";
import React, { useState, useEffect } from "react";
import { FiCheck } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { format } from "date-fns";
import { toast } from "react-toastify";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };





const STATUS_OPTS = [
  { val:"present", label:"P", color:"var(--green-600)", bg:"var(--green-50)", border:"var(--green-100)" },
  { val:"absent",  label:"A", color:"var(--red-600)",   bg:"var(--red-50)",   border:"var(--red-100)" },
  { val:"late",    label:"L", color:"var(--amber-600)", bg:"var(--amber-50)", border:"var(--amber-100)" },
];

export default function TeacherAttendance() {
  const { userData } = useAuth();
  const { classes } = useClasses();
  const { getAttendance, markAttendance, getStudents } = useData();
  const [selectedClass, setSelectedClass] = useState("");
  const [date,          setDate]          = useState(format(new Date(), "yyyy-MM-dd"));
  const [records,       setRecords]       = useState({});
  const [saving,        setSaving]        = useState(false);


  const [students, setStudents] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if(classes.length>0 && !selectedClass) setSelectedClass(classes[0].id); }, [classes]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selectedClass) getStudents(selectedClass).then(s => setStudents(s.slice(0,50))).catch(()=>setStudents([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { init(); }, [selectedClass, date]);

  const init = () => {
    const init = {};
    students.forEach(s => { init[s.id] = "present"; });
    setRecords(init);
    getAttendance({ classId: selectedClass }).then(d => {
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const recs = Object.entries(records).map(([studentId, status]) => ({
        studentId, status, date, classId: selectedClass,
        markedBy: userData?.uid, markedAt: new Date().toISOString(),
        studentName: students.find(s=>s.id===studentId)?.name||"",
      }));
      await markAttendance(recs);
      toast.success(`Attendance saved for ${format(toDate(date), "MMM d, yyyy")}!`);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const summary = {
    present: Object.values(records).filter(v=>v==="present").length,
    absent:  Object.values(records).filter(v=>v==="absent").length,
    late:    Object.values(records).filter(v=>v==="late").length,
  };

  return (
    <div className="fade-in">
      <div className="page-head">
        <div><h1 className="page-title">✅ Mark Attendance</h1><p className="page-subtitle">Daily attendance tracking</p></div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <><div className="spinner spinner-sm"/>Saving…</> : <><FiCheck size={15}/>Save Attendance</>}
        </button>
      </div>

      {/* Controls */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 180 }}>
              <label className="form-label">Class</label>
              <select className="form-control" value={selectedClass} onChange={e => setSelectedClass(e.target.value)}>
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Date</label>
              <input type="date" className="form-control" value={date} max={format(new Date(),"yyyy-MM-dd")} onChange={e => setDate(e.target.value)} />
            </div>
            <button className="btn btn-secondary" onClick={() => {
              const a = {}; students.forEach(s => { a[s.id]="present"; }); setRecords(a);
              toast.info("All marked present");
            }}>All Present</button>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="g g3" style={{ marginBottom: 16 }}>
        {[
          { label:"Present", val:summary.present, color:"var(--green-600)", bg:"var(--green-50)" },
          { label:"Absent",  val:summary.absent,  color:"var(--red-600)",   bg:"var(--red-50)" },
          { label:"Late",    val:summary.late,    color:"var(--amber-600)", bg:"var(--amber-50)" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: "var(--r8)", padding: "14px 18px", textAlign: "center", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: "1.6rem", fontWeight: 800, color: s.color, fontFamily: "Poppins,sans-serif" }}>{s.val}</div>
            <div style={{ fontSize: ".78rem", color: "var(--gray-500)" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Student table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Students — {format(toDate(date), "MMMM d, yyyy")}</h3>
          <span className="badge badge-blue">{students.length} students</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap" style={{overflowX:"auto"}}>
            <table className="data-table" style={{minWidth:540}}>
              <thead>
                <tr><th>#</th><th>Student</th><th>Roll No.</th><th>Present</th><th>Absent</th><th>Late</th></tr>
              </thead>
              <tbody>
                {students.map((s, i) => (
                  <tr key={s.id}>
                    <td style={{ color: "var(--gray-400)", fontWeight: 600 }}>{i + 1}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                        <div className="avatar av-sm" style={{ background:`hsl(${i*55},60%,88%)`, color:`hsl(${i*55},60%,30%)`, fontSize:".65rem" }}>
                          {s.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
                        </div>
                        <span style={{ fontWeight: 500 }}>{s.name}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: ".82rem", color: "var(--gray-500)" }}>{s.rollNumber}</td>
                    {STATUS_OPTS.map(opt => (
                      <td key={opt.val}>
                        <label style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <input type="radio" name={`att_${s.id}`} value={opt.val}
                            checked={records[s.id] === opt.val}
                            onChange={() => setRecords(p => ({ ...p, [s.id]: opt.val }))}
                            style={{ display: "none" }}/>
                          <div style={{
                            width: 34, height: 34, borderRadius: "var(--r6)",
                            background: records[s.id]===opt.val ? opt.bg : "var(--gray-50)",
                            border: `2px solid ${records[s.id]===opt.val ? opt.color : "var(--border)"}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: records[s.id]===opt.val ? opt.color : "var(--gray-400)",
                            fontWeight: 800, fontSize: ".8rem", transition: "var(--t)",
                          }}>{opt.label}</div>
                        </label>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
